import {
  AMENDMENTS_NAV_TABS,
  getTabTitle,
  isAdviceTabGroupPage,
  RECOMMENDATIONS_NAV_TABS,
} from './adviceTabGroups';
import { SETTINGS_CATEGORIES, SETTINGS_ENTRIES } from './settingsNav';

/**
 * Central nav config: pages (tabs), section lists per page, and client-required tabs.
 * Used to build the app sidebar and breadcrumb. Visibility is filtered by userTeamService.getAccessibleTabs.
 */

// Tab names that require a selected client (sidebar disables them; pages show the
// shared "select a client" screen). The single source: FinancialAdviserApp uses it.
export const TABS_REQUIRING_CLIENT = [
  'clientOverview',
  'adviceDates',
  'factFind',
  'reverseFactFind',
  'advice',
  'guidedAdvice',
  'amendments',
  'auditLog',
  'fileNotes',
];

/** Fact Find section metadata (id + title). Used for default sidebar order and for mapping admin section order to titles. */
export const FACT_FIND_SECTIONS = [
  { id: 'personal', title: 'Personal Information' },
  { id: 'employment', title: 'Employment' },
  { id: 'financial', title: 'Income' },
  { id: 'assets', title: 'Assets & Liabilities' },
  { id: 'superannuation', title: 'Superannuation' },
  { id: 'contributions', title: 'Contributions' },
  { id: 'insurance', title: 'Insurance' },
  { id: 'beneficiaries', title: 'Beneficiaries' },
  { id: 'goals', title: 'Goals' },
  { id: 'riskProfile', title: 'Risk Profile Questionnaire' },
];

// SOA Wizard section metadata (ids must match Advice.jsx allFormSections and adviceConfigService section order).
export const ADVICE_SECTIONS = [
  { id: 'clientInsuranceGoal', title: 'Client Insurance Goal' },
  { id: 'insuranceOtherRelevantDetails', title: 'Other Relevant Details' },
  { id: 'insuranceNeedsAnalysis', title: 'Insurance Needs Analysis' },
  { id: 'recommendedInsurance', title: 'Recommended Insurance' },
  { id: 'quotingOptions', title: 'Quoting Options' },
  { id: 'alternativeInsurance', title: 'Insurer Comparison' },
  { id: 'pporLoanPayoff', title: 'Debt Management' },
  { id: 'fhss', title: 'FHSS' },
  { id: 'clientSuperGoals', title: 'Client Super Goals' },
  { id: 'currentPortfolio', title: 'Current Superannuation' },
  { id: 'alternativePortfolio', title: 'Alternative Superannuation' },
  { id: 'likeForLikePortfolio', title: 'Like for Like Superannuation' },
  { id: 'projectionCharts', title: 'Projection Charts' },
  { id: 'recommendedContributions', title: 'Recommended Contributions' },
  { id: 'ebaArrangements', title: 'EBA Arrangements' },
  { id: 'adviceFees', title: 'Advice Fees' },
];

/** Reverse Fact Find section metadata (ids must match ReverseFactFindPage sidebarSections). */
export const REVERSE_FACT_FIND_SECTIONS = [
  { id: 'changes', title: 'Changed Circumstances' },
  { id: 'personal', title: 'Personal Information' },
  { id: 'employment', title: 'Employment' },
  { id: 'financial', title: 'Income' },
  { id: 'assets', title: 'Assets & Liabilities' },
  { id: 'superannuation', title: 'Superannuation' },
  { id: 'insurance', title: 'Insurance' },
  { id: 'contributions', title: 'Contributions' },
  { id: 'beneficiaries', title: 'Beneficiaries' },
  { id: 'goals', title: 'Goals & Objectives' },
  { id: 'riskProfile', title: 'Risk Profile Questionnaire' },
];

// Full page list: tabName, label, icon (lucide name), optional sections.
// Groups (Sept 2026): Workspace (everyday, not client-bound) / Client (needs a
// selected client) / Tools / Reports (was Oversight until Oct 2026) / Support / Settings (one sidebar entry;
// the pages live in the Settings hub, see ./settingsNav.js). Page ids never
// change: they are stored in Page Visibility and checked by the server.
export const ALL_PAGES = [
  // Workspace
  { tabName: 'advisoryPortal', label: 'Advisory Portal', icon: 'Briefcase', sections: [], navGroup: 'Workspace' },
  { tabName: 'paraplannerPortal', label: 'Paraplanner Portal', icon: 'FileText', sections: [], navGroup: 'Workspace' },
  { tabName: 'sendEnvelope', label: 'Marketing Portal', icon: 'Send', sections: [], navGroup: 'Workspace' },
  { tabName: 'complianceDashboard', label: 'Compliance', icon: 'Shield', sections: [], navGroup: 'Workspace' },
  { tabName: 'email', label: 'Email', icon: 'Mail', sections: [], navGroup: 'Workspace' },
  { tabName: 'calendar', label: 'Calendar', icon: 'Calendar', sections: [], navGroup: 'Workspace' },
  { tabName: 'messagesInbox', label: 'Calls & Messages', icon: 'MessagesSquare', sections: [], navGroup: 'Workspace' },
  { tabName: 'smsBroadcast', label: 'Broadcast SMS', icon: 'Users', sections: [], navGroup: 'Workspace' },
  { tabName: 'eSignatures', label: 'Docusign', icon: 'FileSignature', sections: [], navGroup: 'Workspace' },
  { tabName: 'documents', label: 'Document Storage', icon: 'FolderOpen', sections: [], navGroup: 'Workspace' },
  // Client (label shows the selected client's name in the sidebar)
  { tabName: 'clientOverview', label: 'Overview', icon: 'LayoutDashboard', sections: [], navGroup: 'Client' },
  { tabName: 'adviceDates', label: 'Advice History', icon: 'CalendarClock', sections: [], navGroup: 'Client' },
  { tabName: 'factFind', label: 'Fact Find', icon: 'ClipboardList', sections: FACT_FIND_SECTIONS, navGroup: 'Client' },
  { tabName: 'reverseFactFind', label: 'Reverse Fact Find', icon: 'FileInput', sections: REVERSE_FACT_FIND_SECTIONS, navGroup: 'Client' },
  { tabName: 'advice', label: 'Recommendations', icon: 'FileCheck', sections: RECOMMENDATIONS_NAV_TABS, navGroup: 'Client' },
  // Guided Advice (Oct 2026): granted per person in Page Visibility; in no role's defaults.
  { tabName: 'guidedAdvice', label: 'Para', icon: 'MessagesSquare', sections: [], navGroup: 'Client' },
  { tabName: 'amendments', label: 'Amendments', icon: 'FileEdit', sections: AMENDMENTS_NAV_TABS, navGroup: 'Client' },
  { tabName: 'fileNotes', label: 'File Notes', icon: 'StickyNote', sections: [], navGroup: 'Client' },
  { tabName: 'auditLog', label: 'Audit Log', icon: 'History', sections: [], navGroup: 'Client' },
  // Tools
  { tabName: 'fileNote', label: 'SOA → File Note', icon: 'FileText', sections: [], navGroup: 'Tools' },
  { tabName: 'csvMerge', label: 'Import & Release', icon: 'Table2', sections: [], navGroup: 'Tools' },
  // Reports (Oct 2026): question-led reports, one per team (src/constants/reportDefinitions.js)
  { tabName: 'reportsMarketing', label: 'Marketing', icon: 'Megaphone', sections: [], navGroup: 'Reports' },
  { tabName: 'reportsParaplanning', label: 'Paraplanning', icon: 'FileText', sections: [], navGroup: 'Reports' },
  { tabName: 'reportsAdviceApprovals', label: 'Advice approvals', icon: 'FileCheck', sections: [], navGroup: 'Reports' },
  { tabName: 'reportsImplementation', label: 'Implementation', icon: 'PhoneCall', sections: [], navGroup: 'Reports' },
  { tabName: 'reportsSignedDocuments', label: 'Signed Documents', icon: 'FileSignature', sections: [], navGroup: 'Reports' },
  { tabName: 'reportsFsgEmails', label: 'FSG emails', icon: 'Mail', sections: [], navGroup: 'Reports' },
  { tabName: 'reportsUnconverted', label: 'Unconverted presentations', icon: 'UserX', sections: [], navGroup: 'Reports' },
  // Support
  { tabName: 'guides', label: 'Guides', icon: 'BookOpen', sections: [], navGroup: 'Support' },
  { tabName: 'faqs', label: 'FAQs', icon: 'HelpCircle', sections: [], navGroup: 'Support' },
  { tabName: 'helpDesk', label: 'Help Desk', icon: 'LifeBuoy', sections: [], navGroup: 'Support' },
  // Settings — each id stays a separately permissioned page (Page Visibility rows);
  // the sidebar shows one "Settings" entry and the hub groups them (settingsNav.js).
  { tabName: 'adminTeams', label: 'Teams & Access', icon: 'Users', sections: [], navGroup: 'Settings' },
  { tabName: 'adminPageVisibility', label: 'Page Visibility', icon: 'Eye', sections: [], navGroup: 'Settings' },
  { tabName: 'adminDefaultFilters', label: 'Default Filters', icon: 'SlidersHorizontal', sections: [], navGroup: 'Settings' },
  { tabName: 'adminCorrespondence', label: 'Correspondence', icon: 'Mail', sections: [], navGroup: 'Settings' },
  { tabName: 'adminSmsNumbers', label: 'Phone Service', icon: 'Phone', sections: [], navGroup: 'Settings' },
  { tabName: 'adminIdUploadSms', label: 'Upload Link SMS', icon: 'MessageSquare', sections: [], navGroup: 'Settings' },
  { tabName: 'adminSmsScheduling', label: 'Scheduled SMS', icon: 'CalendarClock', sections: [], navGroup: 'Settings' },
  { tabName: 'adminSystemEmails', label: 'System Emails (now in Correspondence)', icon: 'Mail', sections: [], navGroup: 'Settings' },
  { tabName: 'adminDocusignSettings', label: 'DocuSign Settings', icon: 'FileSignature', sections: [], navGroup: 'Settings' },
  { tabName: 'adminDocuments', label: 'Document Templates', icon: 'Files', sections: [], navGroup: 'Settings' },
  { tabName: 'adminDocumentQa', label: 'Document QA', icon: 'ClipboardCheck', sections: [], navGroup: 'Settings' },
  { tabName: 'adminArDocumentTest', label: 'AR Document Test', icon: 'FlaskConical', sections: [], navGroup: 'Settings' },
  { tabName: 'adminAiPrompts', label: 'AI Prompts', icon: 'Sparkles', sections: [], navGroup: 'Settings' },
  { tabName: 'adminPortfolios', label: 'Portfolios', icon: 'TrendingUp', sections: [], navGroup: 'Settings' },
  { tabName: 'adminFormConfig', label: 'Form Configuration', icon: 'ClipboardList', sections: [], navGroup: 'Settings' },
  { tabName: 'adminWorkflows', label: 'Workflows', icon: 'Workflow', sections: [], navGroup: 'Settings' },
  { tabName: 'adminBanner', label: 'Banner', icon: 'Megaphone', sections: [], navGroup: 'Settings' },
  { tabName: 'adminClientAllocation', label: 'Client Allocation', icon: 'Shuffle', sections: [], navGroup: 'Settings' },
  { tabName: 'adminGuidedSandbox', label: 'Para Sandbox', icon: 'FlaskConical', sections: [], navGroup: 'Settings' },
  { tabName: 'adminSettingsInCode', label: 'Settings in code', icon: 'Braces', sections: [], navGroup: 'Settings' },
  { tabName: 'adminManualOverride', label: 'Manual Override', icon: 'ListOrdered', sections: [], navGroup: 'Settings' },
  { tabName: 'adminParaplannerOverride', label: 'Paraplanner Override', icon: 'UserCog', sections: [], navGroup: 'Settings' },
  { tabName: 'adminClientJson', label: 'Client JSON', icon: 'Braces', sections: [], navGroup: 'Settings' },
  { tabName: 'adminMarketingImports', label: 'Marketing Imports', icon: 'Upload', sections: [], navGroup: 'Settings' },
  { tabName: 'adminDeployments', label: 'Deployments', icon: 'Rocket', sections: [], navGroup: 'Settings' },
];

/** Sidebar group order — the single source for AppSidebar and Page Visibility. */
export const NAV_GROUP_SIDEBAR_ORDER = ['Workspace', 'Client', 'Tools', 'Reports', 'Support', 'Settings'];

/**
 * Retired pages: their ids still open something sensible. `{ home: true }`
 * lands on the user's home page (FinancialAdviserApp resolveRetiredTab).
 */
export const RETIRED_TAB_REDIRECTS = {
  // Advice Cycle page retired; legacy advice-cycle tasks are worked from the
  // portal Tasks panel, My Tasks and the task dialog.
  adviceJourney: { section: 'tasks' },
  // Client Meeting retired (Oct 2026) — replaced by advice scenarios on
  // Recommendations / Amendments; its Methodology sheet moved to Recommendations.
  // It had replaced Quick Quote, Projections (+ Methodology) and Auto-Note (beta),
  // so old links to any of them go home.
  clientMeeting: { home: true },
  methodology: { home: true },
  quickQuote: { home: true },
  projections: { home: true },
  autoNoteBeta: { home: true },
  // The per-client Communications / Client Messages page is now Calls & Messages
  // (Workspace), opened filtered to the selected client. The `messages` id still
  // grants access to it (userTeamService._normalizeAccessibleTabs).
  messages: { tab: 'messagesInbox', section: 'client' },
  // Settings → Fee Disclosure merged into the client's Advice Dates page (Sept 2026).
  adminFeeDisclosure: { tab: 'adviceDates' },
  // The in-app document template editor was retired (Oct 2026): PDF templates live in code.
  adminDocumentCanvas: { tab: 'adminDocuments' },
  // The Oversight pages were replaced by the Reports pages (Oct 2026): each old
  // id opens the report that now answers its questions.
  reportingOnboardingFunnel: { tab: 'reportsMarketing' },
  reportingWorkload: { tab: 'reportsParaplanning' },
  reportingThroughput: { tab: 'reportsParaplanning' },
  reportingRework: { tab: 'reportsAdviceApprovals' },
  reportingCommunications: { tab: 'reportsImplementation' },
};

export const SIDEBAR_ORDER_BY_GROUP = {
  Workspace: ['advisoryPortal', 'paraplannerPortal', 'sendEnvelope', 'complianceDashboard', 'email', 'calendar', 'messagesInbox', 'smsBroadcast', 'eSignatures', 'documents'],
  Client: ['clientOverview', 'adviceDates', 'factFind', 'reverseFactFind', 'advice', 'guidedAdvice', 'amendments', 'fileNotes', 'auditLog'],
  Tools: ['fileNote', 'csvMerge'],
  Reports: ['reportsMarketing', 'reportsParaplanning', 'reportsAdviceApprovals', 'reportsImplementation', 'reportsSignedDocuments', 'reportsFsgEmails', 'reportsUnconverted'],
  Support: ['guides', 'faqs', 'helpDesk'],
  Settings: SETTINGS_ENTRIES.flatMap((entry) => entry.ids),
};

export function getNavGroupForTab(tabName) {
  const page = ALL_PAGES.find((p) => p.tabName === tabName);
  if (!page) return null;
  return page.navGroup || 'Advice';
}

/** Pages grouped for Page Visibility (sidebar order; Settings split by hub category). */
export function buildAdminPageVisibilityGroups() {
  const pageByTab = new Map(ALL_PAGES.map((p) => [p.tabName, p]));
  const toPages = (ids) => ids
    .map((tabName) => pageByTab.get(tabName))
    .filter(Boolean)
    .map((p) => ({ id: p.tabName, label: p.label }));
  return NAV_GROUP_SIDEBAR_ORDER.flatMap((groupLabel) => {
    if (groupLabel === 'Settings') {
      return SETTINGS_CATEGORIES.map((category) => ({
        groupLabel: `Settings · ${category.label}`,
        pages: toPages(SETTINGS_ENTRIES.filter((e) => e.category === category.id).flatMap((e) => e.ids)),
      }));
    }
    return [{ groupLabel, pages: toPages(SIDEBAR_ORDER_BY_GROUP[groupLabel] || []) }];
  }).filter((g) => g.pages.length > 0);
}

/** Flat page list in sidebar order (for save/load). */
export function getAllPagesInSidebarOrder() {
  return buildAdminPageVisibilityGroups().flatMap((g) => g.pages);
}

// Sub-pages (edit screens): not in sidebar; breadcrumb shows parent > sub-page.
export const SUB_PAGE_LABELS = {
  // Former standalone pages, now tabs of Admin → Documents (ids kept as aliases).
  adminDocumentLayout: 'Document Templates',
  adminContentBlocks: 'Document Templates',
  adminDocumentStyling: 'Document Templates',
  adminChartSettings: 'Document Templates',
  adminDocxTemplates: 'Document Templates',
  // Business Setup → Assumptions retired Oct 23 2026 (values in code); the id opens Settings in code.
  adminAssumptions: 'Settings in code',
};

export const SUB_PAGE_PARENT = {
  editRecommendedPortfolio: 'adminPortfolios',
  editAlternativePortfolio: 'adminPortfolios',
};

/**
 * Build nav tree for the sidebar: only pages the user can access, with sections attached.
 * @param {string[]} accessibleTabs - from userTeamService.getAccessibleTabs(user.email)
 * @returns {Array<{ tabName, label, icon, sections }>}
 */
export function getNavTreeForUser(accessibleTabs) {
  if (!Array.isArray(accessibleTabs) || accessibleTabs.length === 0) return [];
  const set = new Set(accessibleTabs);
  return ALL_PAGES.filter((p) => set.has(p.tabName));
}

/**
 * Get page label for a tab (or sub-page).
 */
export function getPageLabel(tabName) {
  const page = ALL_PAGES.find((p) => p.tabName === tabName);
  if (page) return page.label;
  return SUB_PAGE_LABELS[tabName] || tabName;
}

/** View-as role keys (admin only) and their display labels */
export const VIEW_AS_ROLE_LABELS = {
  ga: 'GA',
  paraplanner: 'Paraplanner',
  paraplannerManagement: 'Paraplanner Mgmt',
  adviser: 'Adviser',
  ufSupport: 'UF Support',
  arSupport: 'AR Support',
  arAdviser: 'AR Adviser',
  management: 'Management',
  compliance: 'Compliance'
};

/** Default tab (portal) for each view-as role when admin "views as" that role */
export function getDefaultTabForViewAsRole(viewAsKey) {
  const map = {
    ga: 'sendEnvelope',
    paraplanner: 'paraplannerPortal',
    paraplannerManagement: 'paraplannerPortal',
    adviser: 'advisoryPortal',
    ufSupport: 'advisoryPortal',
    arSupport: 'advisoryPortal',
    arAdviser: 'advisoryPortal',
    management: 'advisoryPortal',
    compliance: 'complianceDashboard',
  };
  return map[viewAsKey] || 'advisoryPortal';
}

/**
 * Get section title for a page and section id.
 */
export function getSectionTitle(tabName, sectionId) {
  if (isAdviceTabGroupPage(tabName)) {
    return getTabTitle(tabName, sectionId);
  }
  const page = ALL_PAGES.find((p) => p.tabName === tabName);
  if (!page || !page.sections) return sectionId;
  const section = page.sections.find((s) => s.id === sectionId);
  return section ? section.title : sectionId;
}

/**
 * Whether the section should appear in the breadcrumb (only when it exists in the sidebar for that tab).
 */
export function isSectionInSidebar(tabName, sectionId) {
  if (!tabName || !sectionId) return false;
  const page = ALL_PAGES.find((p) => p.tabName === tabName);
  if (!page || !page.sections || page.sections.length === 0) return false;
  return page.sections.some((s) => s.id === sectionId);
}
